/**
 * Building consent decisions from what the banner actually offered.
 *
 * A category the banner never showed cannot have been consented to, so every
 * decision is built from config.categories_enabled: Accept all grants the
 * enabled categories only, and a category that is not enabled is always
 * refused, whatever an older stored decision says.
 */

import type { ConsentDecisions } from "./StorageAdapter";

export const OPTIONAL_CATEGORIES = ["analytics", "advertising", "functional", "social"] as const;
export type OptionalCategory = (typeof OPTIONAL_CATEGORIES)[number];

interface CategoryConfig {
  categories_enabled: string[];
}

export function enabledCategories(config: CategoryConfig | null): Set<OptionalCategory> {
  // No config (a region that needs no consent) means nothing was withheld.
  if (!config) return new Set(OPTIONAL_CATEGORIES);
  const enabled = new Set(config.categories_enabled ?? []);
  return new Set(OPTIONAL_CATEGORIES.filter((c) => enabled.has(c)));
}

function build(pick: (cat: OptionalCategory) => boolean, config: CategoryConfig | null): ConsentDecisions {
  const enabled = enabledCategories(config);
  const on = (cat: OptionalCategory) => enabled.has(cat) && pick(cat);
  return {
    strictly_necessary: true,
    analytics: on("analytics"),
    advertising: on("advertising"),
    functional: on("functional"),
    social: on("social"),
  };
}

/** Accept all: every category the banner offered, and nothing else. */
export function acceptAllDecisions(config: CategoryConfig | null): ConsentDecisions {
  return build(() => true, config);
}

/** Reject all: strictly necessary only. */
export function rejectAllDecisions(): ConsentDecisions {
  return build(() => false, null);
}

/** Save: the visitor's toggles for offered categories, refused for the rest. */
export function savedDecisions(checked: (cat: OptionalCategory) => boolean, config: CategoryConfig | null): ConsentDecisions {
  return build(checked, config);
}

/** Drop any consent for a category the current config does not offer. */
export function clampDecisions(decisions: ConsentDecisions, config: CategoryConfig | null): ConsentDecisions {
  return build((cat) => decisions[cat] === true, config);
}

/**
 * Categories the visitor had consented to and has now refused. Only offered
 * categories count: a legacy stored `true` for a category the banner does not
 * show was never released (see clampDecisions), so dropping it on the next
 * Save is not a withdrawal and must not reload the page.
 */
export function withdrawnCategories(
  prev: ConsentDecisions | null,
  next: ConsentDecisions,
  config: CategoryConfig | null
): OptionalCategory[] {
  if (!prev) return [];
  const enabled = enabledCategories(config);
  return OPTIONAL_CATEGORIES.filter((c) => enabled.has(c) && prev[c] === true && !next[c]);
}
